/**
 * CapType - Enumeration of cap styles
 *
 * Defines how the ends of slice planes are shaped.
 * Different cap styles affect both aesthetics and structural properties.
 *
 * Based on cc.sketchchair.core.CrossSliceSelection.java
 */

/**
 * Cap type enumeration
 * @readonly
 * @enum {number}
 */
export const CapType = Object.freeze({
    /**
     * Rounded/winged caps with bezier curves
     * Visual: Smooth flowing ends with bezier curves
     * Properties:
     * - Smooth, organic appearance
     * - Extends beyond layer boundaries
     * - Uses bezier control points for curves
     * - Best for aesthetic designs
     */
    CAP_CURVE: 1,

    /**
     * Square/straight caps
     * Visual: Straight, flat ends
     * Properties:
     * - Clean, straight edges
     * - Extends beyond layer boundaries
     * - No bezier curves
     * - Best for structural designs
     */
    CAP_BUTT: 2,

    /**
     * Cut-off caps (flush with layers)
     * Visual: Flush with layer boundaries, no extension
     * Properties:
     * - Flush with actual design boundaries
     * - No extension beyond layers
     * - No fake/guide layers added
     * - Best for precise dimensions
     * Special behavior:
     * - Doesn't add temporary end layers
     * - Doesn't remove outer planes
     * - Slots don't make edges
     */
    CAP_INSIDE: 3,

    /**
     * Rounded rectangles with corner radius
     * Visual: Square caps with rounded corners
     * Properties:
     * - Square with rounded corners
     * - Configurable corner radius
     * - Creates bezier arcs only at corners (not along edges)
     * - Offsets points by cornerRad amount
     * - Best for modern, clean aesthetic
     * Note: Uses magic number 0.552 to approximate circular arc with bezier
     */
    CAP_ROUND_SQUARE: 4
});

/**
 * Get human-readable name for cap type
 * @param {number} type - Cap type value
 * @returns {string} Type name
 */
export function getCapTypeName(type) {
    const names = {
        [CapType.CAP_CURVE]: 'Winged',
        [CapType.CAP_BUTT]: 'Square',
        [CapType.CAP_INSIDE]: 'Cut-Off',
        [CapType.CAP_ROUND_SQUARE]: 'Rounded Rectangle'
    };
    return names[type] || 'Unknown';
}

/**
 * Check if cap type uses bezier curves
 * @param {number} type - Cap type value
 * @returns {boolean} True if uses bezier
 */
export function usesBezierCurves(type) {
    return type === CapType.CAP_CURVE || type === CapType.CAP_ROUND_SQUARE;
}

/**
 * Check if cap type extends beyond boundaries
 * @param {number} type - Cap type value
 * @returns {boolean} True if extends
 */
export function extendsBeyondBoundaries(type) {
    return type !== CapType.CAP_INSIDE;
}

/**
 * Get icon name for cap type
 * @param {number} type - Cap type value
 * @returns {string} Icon filename
 */
export function getCapTypeIcon(type) {
    const icons = {
        [CapType.CAP_CURVE]: 'GUI_SLICE_CAP_WING.png',
        [CapType.CAP_BUTT]: 'GUI_SLICE_CAP_SQUARE.png',
        [CapType.CAP_INSIDE]: 'GUI_SLICE_CUTOFF.png',
        [CapType.CAP_ROUND_SQUARE]: 'GUI_SLICE_CAP_ROUNDRECT.png'
    };
    return icons[type] || '';
}
